import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCurrency } from "../App";
import SEO from "../components/SEO";
import { OrganizationSchema, LocalBusinessSchema, BreadcrumbSchema } from "../components/SchemaMarkup";
import {
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Zap,
  Users,
  BarChart3,
  Target,
  Cog,
  LineChart,
  Mail,
  Globe,
  Megaphone,
  Bot,
  Play,
  Star,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// --- Sub-components ---
const HeroSection = () => (
  <section className="relative overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 hero-gradient" />
    <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-optima-cyan/10 rounded-full blur-3xl" />
    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-optima-blue/10 rounded-full blur-3xl" />
    <div className="container-custom relative py-20 lg:py-32">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-optima-blue/10 text-optima-blue text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-optima-blue rounded-full animate-pulse" />
            Your Strategic Growth Partner
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-optima-dark mb-6 leading-tight">
            SEO & Marketing Automation That{" "}
            <span className="gradient-text">Scales Your Business</span>
          </h1>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
            We help businesses in South Africa, UK & USA implement growth systems, marketing automation, and SEO strategies that deliver measurable results. Stop chasing tactics—start building systems.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
              Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-secondary">
              Explore Services
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /> No long-term contracts</div>
            <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /> Results-focused</div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
          <div className="relative rounded-2xl overflow-hidden shadow-strong">
            <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800" alt="Marketing automation dashboard showing lead growth and SEO metrics for South African business" className="w-full h-auto" />
            <div className="absolute inset-0 bg-gradient-to-t from-optima-dark/20 to-transparent" />
          </div>
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.5 }} className="absolute -left-8 bottom-8 bg-white rounded-xl shadow-medium p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><TrendingUp className="w-6 h-6 text-green-600" /></div>
              <div><div className="text-2xl font-bold text-optima-dark">+247%</div><div className="text-sm text-slate-500">Lead Growth</div></div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

const StatsSection = ({ stats }) => (
  <section className="section-alt" data-testid="stats-section">
    <div className="container-custom">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        {stats.map((stat, index) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-optima-blue/10 flex items-center justify-center"><stat.icon className="w-8 h-8 text-optima-blue" /></div>
            <div className="font-heading text-4xl font-bold text-optima-dark mb-2">{stat.value}</div>
            <div className="text-slate-600">{stat.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const AboutSection = () => (
  <section className="section" data-testid="about-section">
    <div className="container-custom">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <span className="badge badge-blue mb-4">About OptimaGrowth</span>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">Your Strategic SEO & Marketing Automation Partner</h2>
          <p className="text-slate-600 mb-6 leading-relaxed">OptimaGrowth is a strategic growth partner based in Cape Town, South Africa, specializing in SEO, marketing automation, CRM setup, and lead generation systems. We serve businesses across South Africa, the UK, and the USA.</p>
          <p className="text-slate-600 mb-8 leading-relaxed">Our team combines 15+ years of marketing expertise with cutting-edge automation technology. From SEO strategy and content marketing to full-funnel CRM automation, we build systems that work 24/7 to generate leads and close deals.</p>
          <div className="space-y-4 mb-8">
            {["Strategic growth planning & execution", "Marketing automation that works while you sleep", "Data-driven decisions, measurable results", "Dedicated partner, not just a vendor"].map((item) => (
              <div key={item} className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-optima-blue flex-shrink-0" /><span className="text-slate-700">{item}</span></div>
            ))}
          </div>
          <Link to="/about" className="inline-flex items-center gap-2 text-optima-blue font-semibold hover:gap-3 transition-all" data-testid="about-learn-more">Learn more about us <ArrowRight className="w-5 h-5" /></Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative">
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600" alt="OptimaGrowth marketing automation and SEO team in Cape Town South Africa" className="rounded-2xl shadow-medium w-full" />
          <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-medium p-6 max-w-xs">
            <div className="flex items-center gap-4 mb-3">
              <img src="https://customer-assets.emergentagent.com/job_optimascale/artifacts/khacej3k_Pappa%20Roots.png" alt="Rudi van Zyl - OptimaGrowth founder and marketing automation strategist" className="w-14 h-14 rounded-full object-cover object-[center_15%]" />
              <div><div className="font-semibold text-optima-dark">Rudi van Zyl</div><div className="text-sm text-slate-500">Founder & Strategist</div></div>
            </div>
            <p className="text-sm text-slate-600 italic">{"\u201C"}We build systems that grow your business while you focus on what matters.{"\u201D"}</p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const ServicesSection = ({ services }) => (
  <section className="section-alt" data-testid="services-section">
    <div className="container-custom">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="badge badge-cyan mb-4">Our Services</span>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">SEO, Marketing Automation & Growth Services</h2>
        <p className="text-slate-600 text-lg">Comprehensive SEO, CRM automation, and lead generation solutions designed to scale your business in South Africa, the UK, and USA.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }}>
            <Link to={service.link} className="card service-card block p-8 h-full" data-testid={`service-card-${index}`}>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-optima-blue to-optima-cyan flex items-center justify-center mb-6"><service.icon className="w-7 h-7 text-white" /></div>
              <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">{service.title}</h3>
              <p className="text-slate-600 mb-4">{service.description}</p>
              <span className="inline-flex items-center gap-2 text-optima-blue font-medium">Learn more <ChevronRight className="w-4 h-4" /></span>
            </Link>
          </motion.div>
        ))}
      </div>
      <div className="text-center mt-12">
        <Link to="/services" className="btn-primary inline-flex items-center gap-2" data-testid="services-view-all">View All Services <ArrowRight className="w-5 h-5" /></Link>
      </div>
    </div>
  </section>
);

const ProcessSection = ({ steps }) => (
  <section className="section" data-testid="process-section">
    <div className="container-custom">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="badge badge-blue mb-4">Our Process</span>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">Our SEO & Marketing Automation Process</h2>
        <p className="text-slate-600 text-lg">A proven methodology that delivers measurable SEO rankings, lead generation, and marketing automation results.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {steps.map((step, index) => (
          <motion.div key={step.number} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className="relative">
            <div className="text-6xl font-heading font-bold text-optima-blue/10 mb-4">{step.number}</div>
            <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">{step.title}</h3>
            <p className="text-slate-600">{step.description}</p>
            {index < steps.length - 1 && <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-optima-blue/20 to-transparent" />}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsSection = ({ testimonials }) => (
  <section className="section-alt" data-testid="testimonials-section">
    <div className="container-custom">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="badge badge-pink mb-4">Testimonials</span>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">What Our Clients Say</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {testimonials.map((testimonial, idx) => (
          <motion.div key={testimonial.author} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }} viewport={{ once: true }} className="bg-white rounded-2xl p-8 shadow-soft" data-testid={`testimonial-${testimonial.author}`}>
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={`star-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}
            </div>
            <p className="text-slate-600 mb-6 italic">{"\u201C"}{testimonial.quote}{"\u201D"}</p>
            <div className="flex items-center gap-4">
              <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover" />
              <div><div className="font-semibold text-optima-dark">{testimonial.author}</div><div className="text-sm text-slate-500">{testimonial.role}</div></div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="section bg-optima-dark" data-testid="cta-section">
    <div className="container-custom text-center">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="max-w-3xl mx-auto">
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">Ready to Build Your Growth System?</h2>
        <p className="text-white/80 text-lg mb-6">Book a free strategy call and discover how we can help you implement systems that scale your business.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 text-white/70">
          <a href="mailto:info@optimagrowth.agency" className="hover:text-white transition-colors">info@optimagrowth.agency</a>
          <span className="hidden sm:block">&bull;</span>
          <a href="tel:+27661229790" className="hover:text-white transition-colors">+27 66 122 9790</a>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors" data-testid="cta-book-call">Book a Free Growth Audit <ArrowRight className="w-5 h-5" /></Link>
          <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors" data-testid="cta-view-pricing">View Pricing</Link>
        </div>
        <div className="flex flex-wrap justify-center gap-4 mt-6">
          <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Our Blog</Link>
          <Link to="/case-studies" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Case Studies</Link>
          <Link to="/software" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Our Software</Link>
          <Link to="/client-onboarding" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Client Onboarding</Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const HomePage = () => {
  const { formatPrice } = useCurrency();

  const stats = [
    { value: "300%", label: "Average Lead Increase", icon: TrendingUp },
    { value: "24/7", label: "Automated Systems", icon: Zap },
    { value: "50+", label: "Businesses Scaled", icon: Users },
    { value: "15+", label: "Years Experience", icon: BarChart3 },
  ];

  const services = [
    {
      icon: Target,
      title: "Business Growth Strategy",
      description: "Strategic planning and implementation to accelerate your business growth.",
      link: "/services/business-growth-strategy",
    },
    {
      icon: Cog,
      title: "Marketing Automation",
      description: "Build systems that nurture leads and convert prospects while you sleep.",
      link: "/services/marketing-automation",
    },
    {
      icon: LineChart,
      title: "Sales Funnels & CRM",
      description: "Convert more leads with optimized funnels and intelligent CRM systems.",
      link: "/services/sales-funnels-crm",
    },
    {
      icon: Users,
      title: "Lead Generation",
      description: "Attract and qualify high-value prospects with systematic lead gen.",
      link: "/services/lead-generation",
    },
    {
      icon: Globe,
      title: "SEO & Content",
      description: "Dominate search rankings with strategic SEO and content systems.",
      link: "/services/seo-content",
    },
    {
      icon: Megaphone,
      title: "Paid Media",
      description: "High-ROI advertising campaigns on Meta and Google platforms.",
      link: "/services/paid-media",
    },
  ];

  const processSteps = [
    {
      number: "01",
      title: "Discovery",
      description: "We analyze your business, understand your goals, and identify growth opportunities.",
    },
    {
      number: "02",
      title: "Strategy",
      description: "We create a custom growth roadmap with clear milestones and measurable outcomes.",
    },
    {
      number: "03",
      title: "Implementation",
      description: "Our team builds and deploys your growth systems with precision and speed.",
    },
    {
      number: "04",
      title: "Optimization",
      description: "We continuously monitor, test, and improve for maximum performance.",
    },
  ];

  const testimonials = [
    {
      quote: "OptimaGrowth transformed our lead generation process. Their automation systems work around the clock, and we've seen a 300% increase in qualified leads.",
      author: "Sarah Mitchell",
      role: "CEO, TechFlow Solutions",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100",
    },
    {
      quote: "The ROI from their marketing automation has been incredible. We've cut our manual work by 60% while doubling our conversion rates.",
      author: "David Chen",
      role: "Founder, GrowthLabs",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100",
    },
    {
      quote: "Professional, strategic, and results-driven. OptimaGrowth delivered exactly what they promised—and then some.",
      author: "Michelle Roberts",
      role: "Marketing Director, Elevate Brands",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100",
    },
  ];

  return (
    <div className="pt-32" data-testid="home-page">
      <SEO 
        title="SEO & Marketing Automation Agency South Africa"
        description="OptimaGrowth builds automated growth systems — SEO, marketing automation, CRM, lead generation, and AI-powered strategies for businesses in South Africa, UK & USA. Book a free growth audit."
        keywords="growth partner South Africa, marketing automation agency, lead generation, CRM automation, SEO services Cape Town, digital marketing, AI marketing, business growth systems"
      />
      <OrganizationSchema />
      <LocalBusinessSchema />
      <BreadcrumbSchema items={[{ name: "Home" }]} />
      <HeroSection />
      <StatsSection stats={stats} />
      <AboutSection />
      <ServicesSection services={services} />
      <ProcessSection steps={processSteps} />
      <TestimonialsSection testimonials={testimonials} />
      <CTASection />
    </div>
  );
};

export default HomePage;
